// Live-preview host derivation.
//
// Preview URLs (HLS playlist / MSE websocket) must point at the edge that is
// actually serving the stream. That host is reported by the stats.castr.io pulse
// response (`StreamStatsService.getStreamMediaPulse` exposes it as
// `mediaPulse.hostId`, sourced from the pulse's `stream_id`). The old code
// hardcoded a dead staging edge host; this helper mirrors the derivation
// StreamPlayer / StreamPlayerDynamic / ShowcaseThumb already use so every
// preview builder resolves the same live host.

// Load-balancer host id shape carried by the pulse (`wowza-1`, `edge-12`, ...).
// No `g` flag: a shared regex with `g` keeps `lastIndex` state across `.test()`.
const HOST_ID_PATTERN = /\w+-\d+/i

/**
 * Resolve the live serving host for preview URLs from the pulse response.
 * Returns `${hostId}.castr.io`, or `null` when the host isn't known yet so
 * callers can leave the preview empty instead of emitting a URL pointed at the
 * dead staging host.
 *
 * @param {object} [mediaPulse] pulse object from StreamStatsService
 * @param {object} [stream] the stream (used for the pull-URL fallback)
 * @returns {string|null}
 */
export function derivePreviewHost(mediaPulse, stream) {
	const hostId = mediaPulse && mediaPulse.hostId
	if (!hostId) return null
	if (HOST_ID_PATTERN.test(hostId) || (stream && stream.pullUrl)) {
		return `${hostId}.castr.io`
	}
	return null
}

/**
 * Build the `{ type, url }` preview spec for a stream key from the pulse.
 * HLS when the pulse reports a Wowza edge, MSE otherwise. Returns an empty spec
 * (`{ type: '', url: '' }`) until both the host and the key are known.
 *
 * @param {object} [mediaPulse]
 * @param {string} [key] the stream/playout key
 * @param {object} [stream] the stream (used for the pull-URL fallback)
 * @returns {{ type: string, url: string }}
 */
export function buildStreamPreviewSpecs(mediaPulse, key, stream) {
	const host = derivePreviewHost(mediaPulse, stream)
	if (!host || !key) return { type: '', url: '' }
	if (mediaPulse && mediaPulse.isWowza === true) {
		return { type: 'hls', url: `https://${host}/static/${key}/playlist_sfm4s.m3u8` }
	}
	return { type: 'mse', url: `wss://${host}/${key}/mse_ld?tracks=v1a1` }
}
